import { useState, useEffect, useRef, useMemo } from "react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download, Search, Image, Mic, FileIcon, Loader2, Share2, RefreshCw } from "lucide-react";
import { ShareDialog } from "@/components/ShareDialog";
import { ReprocessChatButton } from "@/components/ReprocessChatButton";
import { PdfExportDialog, PdfExportOptions } from "@/components/PdfExportDialog";
import ChatMessage from "@/components/ChatMessage";
import FilterBar from "@/components/FilterBar";
import AiChatPanel from "@/components/AiChatPanel";
import Timeline from "@/components/Timeline";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { parseWhatsAppZip, WhatsAppMessage, ParsedChat } from "@/utils/whatsappParser";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { CostConfirmDialog } from "@/components/CostConfirmDialog";
import { CostTracker } from "@/components/CostTracker";
import { estimateRerunCaptionCost } from "@/utils/costEstimation";

// Mock data - will be replaced with real parsed data
const mockMessages: WhatsAppMessage[] = [
  {
    id: "1",
    sender: "John Doe",
    content: "Hey! How are you doing?",
    timestamp: new Date("2024-01-15T10:30:00"),
    isSent: false,
  },
  {
    id: "2",
    sender: "You",
    content: "I'm great! Just working on an exciting project.",
    timestamp: new Date("2024-01-15T10:31:00"),
    isSent: true,
  },
];

const Viewer = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [messages, setMessages] = useState<WhatsAppMessage[]>(mockMessages);
  const [stats, setStats] = useState({
    totalMessages: 0,
    imageCount: 0,
    audioCount: 0,
    documentCount: 0,
    videoCount: 0,
    contactCount: 0,
  });
  const [processing, setProcessing] = useState(true);
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState(t("loading.initializing"));
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiProgress, setAiProgress] = useState(0);
  const [aiStatusMessage, setAiStatusMessage] = useState('');
  const [rerunningAi, setRerunningAi] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [chatFileName, setChatFileName] = useState<string>("");
  const [chatLanguage, setChatLanguage] = useState<string>("en");
  const [attachmentAiData, setAttachmentAiData] = useState<Record<string, any>>({});
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const wsRef = useRef<Map<string, WebSocket>>(new Map());
  const [processingMessages, setProcessingMessages] = useState<Set<string>>(new Set());
  const prevProcessingRef = useRef(false);
  const [showRerunCostDialog, setShowRerunCostDialog] = useState(false);
  const [rerunCost, setRerunCost] = useState(0);
  const [pendingRerunMessageId, setPendingRerunMessageId] = useState<string | null>(null);
  const [rerunBudget, setRerunBudget] = useState<number | undefined>(undefined);
  const [totalCost, setTotalCost] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [costBreakdown, setCostBreakdown] = useState({
    upload: 0,
    chatQueries: 0,
    rerunCaptions: 0,
  });
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showPdfExportDialog, setShowPdfExportDialog] = useState(false);

  // Show toast when processing completes
  useEffect(() => {
    if (prevProcessingRef.current && !aiProcessing && processingMessages.size === 0) {
      toast({
        title: t("toast.processingComplete"),
        description: t("toast.allAttachmentsAnalyzed"),
      });
    }
    prevProcessingRef.current = aiProcessing;
  }, [aiProcessing, processingMessages.size, toast, t]);

  // Filter and search messages - must be before early returns
  const filteredMessages = useMemo(() => {
    let filtered = [...messages];

    // Apply attachment type filter by file extension
    if (activeFilter) {
      filtered = filtered.filter(msg => {
        if (!msg.attachment || !msg.attachment.fileName) return false;
        
        const ext = msg.attachment.fileName.split('.').pop()?.toLowerCase() || '';
        
        if (activeFilter === 'images') {
          return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
        } else if (activeFilter === 'audio') {
          return ['mp3', 'wav', 'm4a', 'ogg', 'opus', 'aac', 'flac'].includes(ext);
        } else if (activeFilter === 'documents') {
          return ['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'rtf', 'odt'].includes(ext);
        } else if (activeFilter === 'videos') {
          return ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', 'mpeg', 'mpg'].includes(ext);
        } else if (activeFilter === 'contacts') {
          return ext === 'vcf';
        }
        return false;
      });
    }

    // Apply date range filter
    if (dateRange?.from) {
      const fromTime = dateRange.from.getTime();
      const toTime = dateRange.to ? dateRange.to.getTime() : fromTime + 86400000; // +1 day if no end date
      
      filtered = filtered.filter(msg => {
        const msgTime = msg.timestamp.getTime();
        return msgTime >= fromTime && msgTime <= toTime;
      });
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(msg => {
        // Search in message content
        if (msg.content.toLowerCase().includes(query)) return true;
        
        // Search in sender name
        if (msg.sender.toLowerCase().includes(query)) return true;
        
        // Search in AI descriptions
        if (msg.attachment?.aiDescription?.toLowerCase().includes(query)) return true;
        
        // Search in transcriptions
        if (msg.attachment?.transcription?.toLowerCase().includes(query)) return true;
        
        return false;
      });
    }

    return filtered;
  }, [messages, activeFilter, dateRange, searchQuery]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) return;

      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .single();

      setIsAdmin(!!data);
    };

    checkAdminStatus();
  }, [user]);

  // Load and parse chat
  useEffect(() => {
    const loadChat = async () => {
      // Read chatId from URL query parameter first, then fallback to sessionStorage
      const urlParams = new URLSearchParams(window.location.search);
      const urlChatId = urlParams.get('id');
      const sessionChatId = sessionStorage.getItem('chatId');
      const storedChatId = urlChatId || sessionChatId;
      
      if (!storedChatId || !user) {
        setProcessing(false);
        return;
      }

      // Store in sessionStorage for future use
      if (urlChatId) {
        sessionStorage.setItem('chatId', urlChatId);
      }

      setChatId(storedChatId);

      try {
        // Get chat record
        const { data: chat, error: chatError } = await supabase
          .from('chat_analyses')
          .select('*')
          .eq('id', storedChatId)
          .maybeSingle();

        if (chatError) {
          console.error('Database error:', chatError);
          throw new Error('Failed to load chat from database');
        }

        if (!chat) {
          toast({
            title: t("toast.chatNotFound"),
            description: t("toast.chatMayBeDeleted"),
            variant: "destructive",
          });
          navigate("/history");
          return;
        }

        setChatFileName(chat.file_name);
        setChatLanguage(chat.language || 'en');

        // Load cost tracking data if exists
        if (chat.cost_tracking) {
          const costData = chat.cost_tracking as any;
          setTotalCost(costData.total || 0);
          setCostBreakdown(costData.breakdown || {
            upload: 0,
            chatQueries: 0,
            rerunCaptions: 0,
          });
        }

        // Load messages from database (already parsed during upload)
        if (chat.messages_data) {
          toast({
            title: t("toast.loadingChat"),
            description: t("toast.retrievingMessages"),
          });

          const parsedMessages = chat.messages_data as any[];
          
          // Load existing AI data from chat record (keyed by message ID)
          const existingAiData = typeof chat.attachment_ai_data === 'object' && chat.attachment_ai_data !== null 
            ? chat.attachment_ai_data as Record<string, any>
            : {};
          
          // Merge AI data with parsed messages
          const messagesWithAi = parsedMessages.map(msg => {
            if (msg.attachment && existingAiData[msg.id]) {
              const aiData = existingAiData[msg.id] as any;
              return {
                ...msg,
                attachment: {
                  ...msg.attachment,
                  aiDescription: aiData.aiDescription,
                  transcription: aiData.transcription
                }
              };
            }
            return msg;
          });
          
          setMessages(messagesWithAi);
          setAttachmentAiData(existingAiData as Record<string, any>);
          
          // Calculate stats from messages
          const stats = {
            totalMessages: parsedMessages.length,
            imageCount: parsedMessages.filter(m => m.attachment?.type === 'image').length,
            audioCount: parsedMessages.filter(m => m.attachment?.type === 'audio').length,
            documentCount: parsedMessages.filter(m => m.attachment?.type === 'document').length,
            videoCount: parsedMessages.filter(m => m.attachment?.type === 'video').length,
            contactCount: parsedMessages.filter(m => m.attachment?.type === 'contact').length,
          };
          setStats(stats);
          setProcessing(false);

          // Load existing AI summary if available
          if (chat.ai_summary) {
            setAiSummary(chat.ai_summary);
          }

          toast({
            title: t("toast.chatLoaded"),
            description: t("toast.foundMessages").replace("{count}", stats.totalMessages.toString()),
          });

          // Process messages with attachments that don't have AI data
          const messagesToProcess = messagesWithAi.filter(msg => 
            msg.attachment && !existingAiData[msg.id]
          );

          if (messagesToProcess.length > 0) {
            setAiProcessing(true);
            setAiStatusMessage(`Processing ${messagesToProcess.length} attachments...`);
            
            // Process each message individually
            messagesToProcess.forEach((msg, index) => {
              processMessageAttachment(storedChatId, msg, index, messagesToProcess.length);
            });
          } else {
            toast({
              title: t("toast.chatLoaded"),
              description: t("toast.allAttachmentsProcessed"),
            });
          }

          // Generate AI summary if not exists
          if (!chat.ai_summary) {
            generateAiSummary(storedChatId, chat);
          } else {
            setAiSummary(chat.ai_summary);
          }
        } else {
          // Legacy support: Download and parse ZIP file if messages_data doesn't exist
          if (!chat.storage_path) {
            throw new Error('No chat data available');
          }

          const { data: fileBlob, error: downloadError } = await supabase.storage
            .from('chat-exports')
            .download(chat.storage_path);

          if (downloadError || !fileBlob) {
            throw new Error('Failed to download file');
          }

          const file = new File([fileBlob], chat.file_name, { type: 'application/zip' });

          toast({
            title: t("toast.processingChatExportFile"),
            description: t("toast.parsingMessages"),
          });

          // Parse the file
          const parsed: ParsedChat = await parseWhatsAppZip(file, (prog, stage) => {
            setProgress(prog);
            setProgressStage(stage);
          });

          // Upload media files and continue with legacy flow
          setProgressStage(t("loading.uploadingMediaFiles"));
          
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            throw new Error('User not authenticated');
          }
          
          const messagesWithStorageUrls = await Promise.all(
            parsed.messages.map(async (msg) => {
              if (msg.attachment?.url && msg.attachment.url.startsWith('blob:')) {
                try {
                  const blobResponse = await fetch(msg.attachment.url);
                  const blob = await blobResponse.blob();
                  
                  const fileExtension = msg.attachment.fileName.split('.').pop() || 'jpg';
                  const storagePath = `${user.id}/${storedChatId}/media/${msg.id}.${fileExtension}`;
                  
                  const { error: uploadError } = await supabase.storage
                    .from('chat-exports')
                    .upload(storagePath, blob, {
                      contentType: blob.type,
                      upsert: true
                    });
                  
                  if (uploadError) {
                    console.error(`Failed to upload ${msg.attachment.fileName}:`, uploadError);
                    return msg;
                  }
                  
                  const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                    .from('chat-exports')
                    .createSignedUrl(storagePath, 31536000);
                  
                  if (signedUrlError || !signedUrlData) {
                    console.error(`Failed to get signed URL for ${msg.attachment.fileName}:`, signedUrlError);
                    return msg;
                  }
                  
                  return {
                    ...msg,
                    attachment: {
                      ...msg.attachment,
                      url: signedUrlData.signedUrl,
                      storagePath: storagePath
                    }
                  };
                } catch (error) {
                  console.error(`Error uploading ${msg.attachment.fileName}:`, error);
                  return msg;
                }
              }
              return msg;
            })
          );
          
          const existingAiData = typeof chat.attachment_ai_data === 'object' && chat.attachment_ai_data !== null 
            ? chat.attachment_ai_data as Record<string, any>
            : {};
          
          const messagesWithAi = messagesWithStorageUrls.map(msg => {
            if (msg.attachment && existingAiData[msg.id]) {
              const aiData = existingAiData[msg.id] as any;
              return {
                ...msg,
                attachment: {
                  ...msg.attachment,
                  aiDescription: aiData.aiDescription,
                  transcription: aiData.transcription
                }
              };
            }
            return msg;
          });
          
          setMessages(messagesWithAi);
          setAttachmentAiData(existingAiData as Record<string, any>);
          setStats(parsed.stats);
          setProcessing(false);

          if (chat.ai_summary) {
            setAiSummary(chat.ai_summary);
          }

          toast({
            title: t("toast.chatLoaded"),
            description: t("toast.foundMessages").replace("{count}", parsed.stats.totalMessages.toString()),
          });

          const htmlContent = generateHTMLContent(messagesWithAi);
          await supabase.from('chat_analyses').update({
            html_content: htmlContent,
            message_count: parsed.stats.totalMessages,
            image_count: parsed.stats.imageCount,
            audio_count: parsed.stats.audioCount,
            document_count: parsed.stats.documentCount,
            processing_status: 'parsed',
            messages_data: messagesWithAi as any,
          }).eq('id', storedChatId);

          const messagesToProcess = messagesWithAi.filter(msg => 
            msg.attachment && !existingAiData[msg.id]
          );

          if (messagesToProcess.length > 0) {
            setAiProcessing(true);
            setAiStatusMessage(`Processing ${messagesToProcess.length} attachments...`);
            
            messagesToProcess.forEach((msg, index) => {
              processMessageAttachment(storedChatId, msg, index, messagesToProcess.length);
            });
          } else {
            toast({
              title: t("toast.chatLoaded"),
              description: t("toast.allAttachmentsProcessed"),
            });
          }

          if (!chat.ai_summary) {
            generateAiSummary(storedChatId, chat);
          } else {
            setAiSummary(chat.ai_summary);
          }
        }
        
      } catch (error) {
        console.error('Load error:', error);
        toast({
          title: t("toast.failedToLoadChat"),
          description: error instanceof Error ? error.message : "An error occurred",
          variant: "destructive",
        });
        setProcessing(false);
      }
    };

    if (user) {
      loadChat();
    }

    // Cleanup WebSocket on unmount
    return () => {
      wsRef.current.forEach(ws => {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      });
      wsRef.current.clear();
    };
  }, [user, toast]);

  // Function to handle cost tracking
  const handleCostIncurred = async (cost: number, type: 'upload' | 'chatQueries' | 'rerunCaptions' | 'rerunSummary') => {
    if (!chatId) return;

    const newBreakdown = {
      ...costBreakdown,
      [type]: (costBreakdown[type] || 0) + cost,
    };
    const newTotal = totalCost + cost;

    setTotalCost(newTotal);
    setCostBreakdown(newBreakdown);

    // Update database
    await supabase
      .from('chat_analyses')
      .update({
        cost_tracking: {
          total: newTotal,
          breakdown: newBreakdown,
          lastUpdated: new Date().toISOString(),
        }
      })
      .eq('id', chatId);
  };

  // Function to process individual message attachment
  const processMessageAttachment = (chatId: string, message: WhatsAppMessage, index: number, total: number) => {
    const wsUrl = `wss://chlqyowphtwbmtnlxqxy.supabase.co/functions/v1/process-message?chatId=${chatId}&messageId=${message.id}`;
    const ws = new WebSocket(wsUrl);
    
    wsRef.current.set(message.id, ws);
    setProcessingMessages(prev => new Set(prev).add(message.id));

    ws.onopen = () => {
      console.log('🔌 WebSocket connected for message:', message.id);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 Message from WS:', data);

      switch (data.type) {
        case 'status':
          setAiStatusMessage(`${data.message} (${index + 1}/${total})`);
          break;

        case 'cached':
          console.log('✅ Loaded from cache:', message.id);
          // Update message with cached data
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === message.id && msg.attachment
                ? {
                    ...msg,
                    attachment: {
                      ...msg.attachment,
                      aiDescription: data.data.aiDescription,
                      transcription: data.data.transcription
                    }
                  }
                : msg
            )
          );
          setProcessingMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(message.id);
            if (newSet.size === 0) setAiProcessing(false);
            return newSet;
          });
          break;

        case 'complete':
          console.log('✅ Processed message:', message.id, data.data);
          // Update attachment_ai_data state
          setAttachmentAiData(prev => ({
            ...prev,
            [message.id]: data.data
          }));
          // Update message with new AI data
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === message.id && msg.attachment
                ? {
                    ...msg,
                    attachment: {
                      ...msg.attachment,
                      aiDescription: data.data.aiDescription,
                      transcription: data.data.transcription
                    }
                  }
                : msg
            )
          );
          setProcessingMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(message.id);
            if (newSet.size === 0) {
              setAiProcessing(false);
            }
            return newSet;
          });
          break;

        case 'error':
          console.error('❌ Error processing message:', message.id, data.message);
          setProcessingMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(message.id);
            if (newSet.size === 0) setAiProcessing(false);
            return newSet;
          });
          break;
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error for message:', message.id, error);
      setProcessingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(message.id);
        if (newSet.size === 0) setAiProcessing(false);
        return newSet;
      });
    };

    ws.onclose = () => {
      console.log('🔌 WebSocket closed for message:', message.id);
      wsRef.current.delete(message.id);
    };
  };

  // Function to initiate rerun - show cost dialog first
  const initiateRerunAI = async (messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message || !message.attachment) return;

    // Fetch current budget
    try {
      if (user) {
        const { data } = await supabase
          .from("profiles")
          .select("budget")
          .eq("id", user.id)
          .single();
        
        if (data) {
          setRerunBudget(parseFloat(String(data.budget || 0)));
        }
      }
    } catch (error) {
      console.error("Error fetching budget:", error);
    }

    // Calculate cost based on attachment type
    const cost = estimateRerunCaptionCost(message.attachment.type as 'image' | 'audio', isAdmin);
    setRerunCost(cost);
    setPendingRerunMessageId(messageId);
    setShowRerunCostDialog(true);
  };

  // Function to rerun AI processing for a specific message
  const handleRerunAI = async (messageId: string) => {
    if (!chatId) return;

    const message = messages.find(m => m.id === messageId);
    if (!message || !message.attachment) return;

    // Check and deduct budget
    try {
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("budget")
          .eq("id", user.id)
          .single();

        if (profile) {
          const currentBudget = parseFloat(String(profile.budget || 0));
          if (currentBudget < rerunCost) {
            toast({
              title: "Insufficient Budget",
              description: "Please add funds to continue.",
              variant: "destructive",
            });
            setShowRerunCostDialog(false);
            return;
          }

          // Deduct from budget
          const newBudget = currentBudget - rerunCost;
          await supabase
            .from("profiles")
            .update({ budget: newBudget })
            .eq("id", user.id);
        }
      }
    } catch (error) {
      console.error("Error handling budget:", error);
      toast({
        title: "Error",
        description: "Failed to process budget",
        variant: "destructive",
      });
      setShowRerunCostDialog(false);
      return;
    }

    try {
      // Clear cached AI data for this message
      const { data: chat, error: fetchError } = await supabase
        .from("chat_analyses")
        .select("attachment_ai_data")
        .eq("id", chatId)
        .single();

      if (fetchError || !chat) {
        toast({
          title: "Error",
          description: "Failed to load chat data",
          variant: "destructive",
        });
        return;
      }

      // Remove this message's AI data from the cache
      const attachmentAiData = (chat.attachment_ai_data as Record<string, any>) || {};
      const updatedAiData = { ...attachmentAiData };
      delete updatedAiData[messageId];

      const { error: updateError } = await supabase
        .from("chat_analyses")
        .update({ attachment_ai_data: updatedAiData })
        .eq("id", chatId);

      if (updateError) {
        toast({
          title: "Error",
          description: "Failed to clear cache",
          variant: "destructive",
        });
        return;
      }

      // Clear AI data from the message state
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId && msg.attachment
            ? {
                ...msg,
                attachment: {
                  ...msg.attachment,
                  aiDescription: undefined,
                  transcription: undefined,
                }
              }
            : msg
        )
      );

      // Re-process the message
      processMessageAttachment(chatId, message, 0, 1);

      // Track cost
      handleCostIncurred(rerunCost, 'rerunCaptions');

      toast({
        title: "AI reprocessing started",
        description: "Generating new analysis...",
      });
    } catch (error) {
      console.error("Error rerunning AI:", error);
      toast({
        title: "Error",
        description: "Failed to rerun AI processing",
        variant: "destructive",
      });
    }
  };

  // Function to generate AI summary
  const generateAiSummary = async (chatId: string, chat: any) => {
    try {
      setAiStatusMessage(t("viewer.generatingSummary"));
      
      const wsUrl = `wss://chlqyowphtwbmtnlxqxy.supabase.co/functions/v1/process-chat-ws?chatId=${chatId}`;
      const ws = new WebSocket(wsUrl);

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'summary') {
          setAiSummary(data.data);
        } else if (data.type === 'complete') {
          setAiStatusMessage('');
        }
      };

      ws.onerror = (error) => {
        console.error('Error generating summary:', error);
      };
    } catch (error) {
      console.error('Failed to generate summary:', error);
    }
  };

  // Function to scroll to a specific message
  const handleScrollToMessage = (messageId: string) => {
    if (!messagesContainerRef.current) return;
    
    // Find the message element
    const messageElement = messagesContainerRef.current.querySelector(
      `[data-message-id="${messageId}"]`
    ) as HTMLElement;
    
    if (messageElement) {
      // Scroll the message into view with smooth behavior
      messageElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Add a highlight effect
      messageElement.style.transition = 'background-color 0.3s ease';
      messageElement.style.backgroundColor = 'hsl(var(--primary) / 0.1)';
      
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
      }, 2000);
    }
  };

  // Function to rerun AI summary
  const handleRerunSummary = async () => {
    if (!chatId || rerunningAi) return;

    try {
      setRerunningAi(true);
      setAiSummary('');
      setAiStatusMessage(t("viewer.regeneratingSummary"));

      const { data, error } = await supabase.functions.invoke('rerun-summary', {
        body: { chatId }
      });

      if (error) {
        console.error('Failed to rerun summary:', error);
        toast({
          title: t("error.title"),
          description: error.message || t("viewer.rerunSummaryError"),
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        toast({
          title: t("error.title"),
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      setAiSummary(data.summary);
      
      // Update cost tracking
      if (data.cost) {
        handleCostIncurred(data.cost, 'rerunSummary');
      }

      toast({
        title: t("viewer.summaryRegenerated"),
        description: t("viewer.summaryRegenerated"),
      });
    } catch (error) {
      console.error('Error rerunning summary:', error);
      toast({
        title: t("error.title"),
        description: t("viewer.rerunSummaryError"),
        variant: "destructive",
      });
    } finally {
      setRerunningAi(false);
      setAiStatusMessage('');
    }
  };

  // Subscribe to real-time AI processing updates (fallback for database updates)
  useEffect(() => {
    if (!chatId || !user) return;

    const channel = supabase
      .channel('chat-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_analyses',
          filter: `id=eq.${chatId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          console.log('📡 Realtime update received:', updated);
          
          // Update AI summary
          if (updated.ai_summary && !aiSummary) {
            setAiSummary(updated.ai_summary);
          }
          
          // Update messages with new AI data from attachments
          if (updated.attachment_ai_data) {
            setMessages(prevMessages => prevMessages.map(msg => {
              if (!msg.attachment) return msg;
              
              const aiData = updated.attachment_ai_data[msg.attachment.fileName];
              if (aiData) {
                console.log(`✅ Updating message ${msg.id} with AI data:`, aiData);
                return {
                  ...msg,
                  attachment: {
                    ...msg.attachment,
                    ...aiData
                  }
                };
              }
              return msg;
            }));
          }
          
          // Update processing status
          if (updated.processing_status === 'completed') {
            setAiProcessing(false);
            setProcessing(false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId, user, aiSummary]);

  const generateHTMLContent = (messages: WhatsAppMessage[]): string => {
    // Generate HTML representation of the chat for storage (with message IDs)
    let html = '<div class="chat-export">';
    messages.forEach(msg => {
      html += `<div class="message ${msg.isSent ? 'sent' : 'received'}" data-id="${msg.id}">`;
      html += `<div class="sender">${msg.sender}</div>`;
      html += `<div class="content">${msg.content}</div>`;
      const timestampStr = typeof msg.timestamp === 'string' ? msg.timestamp : msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date(msg.timestamp).toISOString();
      html += `<div class="timestamp">${timestampStr}</div>`;
      if (msg.attachment) {
        const storagePathAttr = (msg.attachment as any).storagePath ? ` data-storage-path="${(msg.attachment as any).storagePath}"` : '';
        html += `<div class="attachment" data-type="${msg.attachment.type}" data-file="${msg.attachment.fileName}"${msg.attachment.url ? ` data-url="${msg.attachment.url}"` : ''}${storagePathAttr}></div>`;
      }
      html += '</div>';
    });
    html += '</div>';
    return html;
  };

  if (loading || processing) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md w-full px-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <div className="space-y-2">
            <p className="text-lg font-medium">
              {loading ? t("loading.general") : progressStage}
            </p>
            {processing && (
              <>
                <div className="w-full bg-secondary rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="bg-primary h-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">{progress}%</p>
              </>
            )}
          </div>
          {processing && (
            <p className="text-sm text-muted-foreground">
              {t("viewer.extractingMessages")}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleExportPDF = async (options: PdfExportOptions) => {
    let tempDiv: HTMLDivElement | null = null;
    
    try {
      toast({
        title: "מייצר PDF...",
        description: "אנא המתן",
      });

      const html2pdf = (await import('html2pdf.js')).default;
      
      // Escape HTML special characters
      const escapeHtml = (text: string) => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      };
      
      // Filter messages based on options
      let messagesToExport = messages;
      
      // Apply date range filter
      if (options.dateRange?.from) {
        messagesToExport = messagesToExport.filter(msg => {
          const msgDate = new Date(msg.timestamp);
          const from = options.dateRange!.from!;
          const to = options.dateRange!.to || options.dateRange!.from!;
          
          return msgDate >= from && msgDate <= new Date(to.getTime() + 86400000); // Add 1 day to include end date
        });
      }
      
      if (messagesToExport.length === 0) {
        throw new Error('No messages in selected date range');
      }
      
      // Get export date info
      const exportDate = new Date().toLocaleDateString('he-IL');
      const dateRangeText = options.dateRange?.from 
        ? options.dateRange.to
          ? `${format(options.dateRange.from, "dd/MM/yyyy")} - ${format(options.dateRange.to, "dd/MM/yyyy")}`
          : format(options.dateRange.from, "dd/MM/yyyy")
        : "כל התאריכים";
      
      // Create HTML content for PDF with chat-like styling
      let htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl">
        <head>
          <meta charset="UTF-8">
          <style>
            @page {
              size: A4;
              margin: 25mm 20mm 25mm 20mm;
            }
            
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              font-size: 10pt;
              line-height: 1.6;
              color: #1a1a1a;
              direction: rtl;
              background: #ffffff;
            }
            
            .page-header {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              height: 20mm;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 8px 20px;
              border-bottom: 3px solid #5568d3;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .page-header h1 {
              margin: 0;
              font-size: 16pt;
              font-weight: 600;
            }
            
            .page-header .chat-info {
              font-size: 9pt;
              opacity: 0.95;
            }
            
            .page-footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              height: 15mm;
              background: #f8f9fa;
              border-top: 2px solid #e0e0e0;
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0 20px;
              font-size: 9pt;
              color: #666;
            }
            
            .content-area {
              margin-top: 25mm;
              margin-bottom: 20mm;
            }
            
            .message {
              page-break-inside: avoid;
              margin-bottom: 12px;
              padding: 12px 15px;
              border-radius: 12px;
              position: relative;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            
            .message.sent {
              background: linear-gradient(135deg, #dcf8c6 0%, #d4f1c0 100%);
              border-right: 4px solid #34b7f1;
              margin-left: 60px;
            }
            
            .message.received {
              background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
              border-right: 4px solid #9c27b0;
              margin-right: 60px;
            }
            
            .sender {
              font-weight: 700;
              color: #1a1a1a;
              margin-bottom: 4px;
              font-size: 10.5pt;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            
            .sender::before {
              content: "👤";
              font-size: 12pt;
            }
            
            .message.sent .sender {
              color: #075e54;
            }
            
            .message.received .sender {
              color: #6a1b9a;
            }
            
            .content {
              margin: 6px 0;
              word-wrap: break-word;
              white-space: pre-wrap;
              color: #2c2c2c;
              line-height: 1.5;
            }
            
            .timestamp {
              font-size: 8pt;
              color: #777;
              margin-top: 6px;
              display: flex;
              align-items: center;
              gap: 4px;
            }
            
            .timestamp::before {
              content: "🕐";
              font-size: 9pt;
            }
            
            .attachment-info {
              margin-top: 10px;
              padding: 10px;
              background: rgba(255, 255, 255, 0.9);
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              box-shadow: 0 1px 2px rgba(0,0,0,0.05);
            }
            
            .attachment-type {
              font-weight: 700;
              color: #5568d3;
              text-transform: uppercase;
              font-size: 9pt;
              margin-bottom: 4px;
              letter-spacing: 0.5px;
            }
            
            .attachment-filename {
              font-size: 9pt;
              color: #555;
              margin-top: 3px;
            }
            
            .ai-analysis {
              margin-top: 8px;
              padding: 8px 10px;
              background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
              border-right: 3px solid #2196F3;
              border-radius: 6px;
              font-size: 9.5pt;
              line-height: 1.5;
            }
            
            .ai-analysis strong {
              color: #1565c0;
              display: flex;
              align-items: center;
              gap: 4px;
              margin-bottom: 3px;
            }
            
            .ai-analysis strong::before {
              content: "🤖";
              font-size: 10pt;
            }
            
            .transcription {
              margin-top: 8px;
              padding: 8px 10px;
              background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
              border-right: 3px solid #ff9800;
              border-radius: 6px;
              font-size: 9.5pt;
              line-height: 1.5;
            }
            
            .transcription strong {
              color: #e65100;
              display: flex;
              align-items: center;
              gap: 4px;
              margin-bottom: 3px;
            }
            
            .transcription strong::before {
              content: "🎤";
              font-size: 10pt;
            }
            
            img {
              max-width: 100%;
              height: auto;
              margin-top: 10px;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            
            .date-separator {
              text-align: center;
              margin: 20px 0;
              padding: 8px;
              background: #f0f0f0;
              border-radius: 20px;
              font-weight: 600;
              color: #555;
              font-size: 9pt;
            }
          </style>
        </head>
        <body>
          <div class="page-header">
            <div>
              <h1>יצוא שיחה</h1>
              <div class="chat-info">${escapeHtml(chatFileName || 'WhatsApp Chat')}</div>
            </div>
            <div style="text-align: left;">
              <div>תאריך יצוא: ${exportDate}</div>
              <div>טווח: ${dateRangeText}</div>
            </div>
          </div>
          
          <div class="page-footer">
            <div>${escapeHtml(chatFileName || 'WhatsApp Chat')}</div>
            <div class="page-number"></div>
            <div>יוצא באמצעות WhatAI</div>
          </div>
          
          <div class="content-area">
      `;
      
      // Group messages by date for better organization
      let lastDate = '';
      messagesToExport.forEach((msg) => {
        const msgDate = new Date(msg.timestamp);
        const dateStr = msgDate.toLocaleDateString('he-IL', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        
        // Add date separator if date changed
        if (dateStr !== lastDate) {
          htmlContent += `<div class="date-separator">${dateStr}</div>`;
          lastDate = dateStr;
        }
        
        const formattedTime = msgDate.toLocaleTimeString('he-IL', {
          hour: '2-digit',
          minute: '2-digit'
        });
        
        htmlContent += `
          <div class="message ${msg.isSent ? 'sent' : 'received'}">
            <div class="sender">${escapeHtml(msg.sender)}</div>
            <div class="content">${escapeHtml(msg.content)}</div>
            <div class="timestamp">${formattedTime}</div>
        `;
        
        if (msg.attachment) {
          const shouldIncludeAttachment = 
            (msg.attachment.type === 'image' && options.includeImages) ||
            (msg.attachment.type === 'audio' && options.includeAudioTranscriptions) ||
            (msg.attachment.type !== 'image' && msg.attachment.type !== 'audio');
          
          if (shouldIncludeAttachment) {
            htmlContent += `
              <div class="attachment-info">
                <div class="attachment-type">📎 ${msg.attachment.type}</div>
                ${msg.attachment.fileName ? `<div class="attachment-filename">${escapeHtml(msg.attachment.fileName)}</div>` : ''}
            `;
            
            if (msg.attachment.aiDescription && options.includeAiDescriptions) {
              htmlContent += `
                <div class="ai-analysis">
                  <strong>ניתוח AI</strong>
                  <div>${escapeHtml(msg.attachment.aiDescription)}</div>
                </div>
              `;
            }
            
            if (msg.attachment.transcription && options.includeAudioTranscriptions) {
              htmlContent += `
                <div class="transcription">
                  <strong>תמלול אודיו</strong>
                  <div>${escapeHtml(msg.attachment.transcription)}</div>
                </div>
              `;
            }
            
            htmlContent += `</div>`;
          }
        }
        
        htmlContent += `</div>`;
      });
      
      htmlContent += `
          </div>
        </body>
        </html>
      `;
      
      // Create temporary element
      tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.innerHTML = htmlContent;
      document.body.appendChild(tempDiv);
      
      // Wait for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // PDF options
      const opt = {
        margin: 0,
        filename: `${chatFileName || 'chat-export'}_${format(new Date(), 'yyyy-MM-dd')}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { 
          scale: 2.5,
          useCORS: true,
          logging: false,
          windowWidth: 800,
          letterRendering: true,
          backgroundColor: '#ffffff'
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], after: '.date-separator' }
      };
      
      // Generate PDF
      console.log('Starting PDF generation...');
      const pdfWorker = html2pdf().set(opt).from(tempDiv);
      
      await pdfWorker.toPdf().get('pdf').then(function (pdf: any) {
        const totalPages = pdf.internal.getNumberOfPages();
        console.log(`Generated ${totalPages} pages`);
        
        // Add page numbers to footer
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(9);
          pdf.setTextColor(102, 102, 102);
          
          // Page number in footer
          const pageWidth = pdf.internal.pageSize.getWidth();
          pdf.text(`עמוד ${i} מתוך ${totalPages}`, pageWidth / 2, pdf.internal.pageSize.getHeight() - 10, { align: 'center' });
        }
      });
      
      // Save the PDF
      await pdfWorker.save();
      
      toast({
        title: "PDF מוכן",
        description: `${messagesToExport.length} הודעות יוצאו בהצלחה`,
      });
      
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast({
        title: "שגיאה ביצוא",
        description: error instanceof Error ? error.message : "היצוא נכשל",
        variant: "destructive",
      });
    } finally {
      // Cleanup
      if (tempDiv && document.body.contains(tempDiv)) {
        document.body.removeChild(tempDiv);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="bg-card border-b shadow-soft sticky top-0 z-10">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl sm:text-2xl font-bold text-primary">
                {t("viewer.chatAnalysis")}
              </h1>
              {chatFileName && (
                <p className="text-sm text-muted-foreground truncate">
                  {chatFileName}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {chatId && (
                <ReprocessChatButton chatId={chatId} />
              )}
              <Button onClick={() => setShowShareDialog(true)} variant="outline" size="sm">
                <Share2 className="mr-2 h-4 w-4" />
                {t("share.shareChat")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Share Dialog */}
      {chatId && (
        <ShareDialog 
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          chatId={chatId}
        />
      )}

      {/* Filter Bar */}
      <FilterBar 
        activeFilter={activeFilter} 
        onFilterChange={setActiveFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        onExportPDF={() => handleExportPDF({ includeImages: true, includeAudioTranscriptions: true, includeAiDescriptions: true })}
        onOpenExportDialog={() => setShowPdfExportDialog(true)}
      />

      {/* PDF Export Dialog */}
      <PdfExportDialog
        open={showPdfExportDialog}
        onOpenChange={setShowPdfExportDialog}
        onExport={handleExportPDF}
        currentDateRange={dateRange}
        messages={messages}
        chatFileName={chatFileName}
      />

      {/* AI Chat Panel - Fixed position floating overlay */}
      <div className="fixed bottom-24 left-4 z-30">
        <AiChatPanel 
          chatContext={JSON.stringify({
            fileName: chatFileName,
            messageCount: stats.totalMessages,
            messages: messages.map(m => ({
              sender: m.sender,
              content: m.content,
              timestamp: typeof m.timestamp === 'string' ? m.timestamp : m.timestamp instanceof Date ? m.timestamp.toISOString() : new Date(m.timestamp).toISOString(),
              attachment: m.attachment ? {
                type: m.attachment.type,
                aiDescription: m.attachment.aiDescription,
                transcription: m.attachment.transcription
              } : undefined
            })),
            aiSummary
          })}
          language={chatLanguage}
          onCostIncurred={(cost) => handleCostIncurred(cost, 'chatQueries')}
        />
      </div>

      {/* Cost Confirmation Dialogs */}
      <CostConfirmDialog
        open={showRerunCostDialog}
        onOpenChange={setShowRerunCostDialog}
        onConfirm={() => {
          setShowRerunCostDialog(false);
          if (pendingRerunMessageId) {
            handleRerunAI(pendingRerunMessageId);
            setPendingRerunMessageId(null);
          }
        }}
        cost={rerunCost}
        title={t("viewer.confirmRerunTitle")}
        description={t("viewer.confirmRerunDesc")}
        currentBudget={rerunBudget}
      />

      {/* Main Content */}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Chat Messages */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-xl sm:rounded-2xl shadow-soft p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl font-bold">{t("viewer.messages")}</h2>
                {(activeFilter || dateRange?.from || searchQuery) && (
                  <p className="text-sm text-muted-foreground">
                    {t("viewer.showingMessages")
                      .replace("{filtered}", filteredMessages.length.toString())
                      .replace("{total}", messages.length.toString())}
                  </p>
                )}
              </div>
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">{t("viewer.noMessages")}</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate("/upload")}
                  >
                    {t("viewer.uploadChat")}
                  </Button>
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">{t("viewer.noMatchingMessages")}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => {
                      setActiveFilter(null);
                      setDateRange(undefined);
                      setSearchQuery("");
                    }}
                  >
                    {t("viewer.clearFilters")}
                  </Button>
                </div>
              ) : (
                <div 
                  ref={messagesContainerRef}
                  className="space-y-3 sm:space-y-4 max-h-[500px] sm:max-h-[600px] overflow-y-auto"
                >
                  {filteredMessages.map((message) => (
                    <div key={message.id} data-message-id={message.id}>
                      <ChatMessage 
                        message={message}
                        isProcessing={processingMessages.has(message.id)}
                        onRerunAI={initiateRerunAI}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
              <div className="bg-card rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-chat">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary mb-1 sm:mb-2" />
                <p className="text-xl sm:text-2xl font-bold">{stats.totalMessages}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.messagesLabel")}</p>
              </div>
              <div className="bg-card rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-chat">
                <Image className="w-5 h-5 sm:w-6 sm:h-6 text-accent mb-1 sm:mb-2" />
                <p className="text-xl sm:text-2xl font-bold">{stats.imageCount}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.imagesLabel")}</p>
              </div>
              <div className="bg-card rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-chat">
                <Mic className="w-5 h-5 sm:w-6 sm:h-6 text-primary mb-1 sm:mb-2" />
                <p className="text-xl sm:text-2xl font-bold">{stats.audioCount}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.audioLabel")}</p>
              </div>
              <div className="bg-card rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-chat">
                <FileIcon className="w-5 h-5 sm:w-6 sm:h-6 text-accent mb-1 sm:mb-2" />
                <p className="text-xl sm:text-2xl font-bold">{stats.documentCount}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.documentsLabel")}</p>
              </div>
            </div>
          </div>

          {/* Sidebar - Timeline & Summary */}
          <div className="space-y-4 sm:space-y-6">
            <Timeline 
              messages={messages} 
              onDateClick={handleScrollToMessage}
            />
            
            {/* Cost Tracker */}
            <CostTracker 
              totalCost={totalCost} 
              breakdown={costBreakdown}
            />
            
            <div className="bg-card rounded-xl sm:rounded-2xl shadow-soft p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <h3 className="text-base sm:text-lg font-bold">{t("viewer.aiSummaryTitle")}</h3>
                {aiSummary && !aiProcessing && !rerunningAi && (
                  <Button
                    onClick={handleRerunSummary}
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    {t("viewer.rerunSummary")}
                  </Button>
                )}
              </div>
              {aiProcessing || rerunningAi ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {aiStatusMessage || t("viewer.analyzingConversation")}
                    </p>
                  </div>
                  {aiProgress > 0 && (
                    <div className="space-y-1">
                      <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-primary h-full transition-all duration-300 ease-out"
                          style={{ width: `${aiProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground text-right">{aiProgress}%</p>
                    </div>
                  )}
                </div>
              ) : aiSummary ? (
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {aiSummary}
                </p>
              ) : (
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t("viewer.noSummaryYet")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Viewer;
